import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, LinearProgress, Alert, Stack, Paper, Typography,
  Button, IconButton, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import RecipeManagerV2 from './RecipeManagerV2';            // ⬅️ dessert-only manager
import baseSchema from './schemas/dessert_schema.json';
import { db } from './firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject
} from 'firebase/storage';

/* ----------------- helpers ----------------- */
const deepClone = (o) => JSON.parse(JSON.stringify(o));
const slug = (s) => String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const slugFile = (s) => String(s || 'file').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9._-]/g, '-');

const buildChoices = (list, idKey, labelKeys) => {
  const ids = [], titles = [], oneOf = [];
  for (const row of list || []) {
    const id = String(row?.[idKey] ?? '').trim();
    if (!id) continue;
    const title = (labelKeys || []).map(k => String(row?.[k] ?? '').trim()).find(Boolean) || id;
    ids.push(id); titles.push(title); oneOf.push({ const: id, title });
  }
  return { ids, titles, oneOf };
};
const applyChoices = (propNode, list, idKey, labelKeys) => {
  if (!propNode) return;
  const { ids, titles, oneOf } = buildChoices(list, idKey, labelKeys);
  if (!ids.length) { delete propNode.enum; delete propNode.enumNames; delete propNode.oneOf; return; }
  propNode.enum = ids; propNode.enumNames = titles; propNode.oneOf = oneOf;
};
const scrubEmptyEnums = (node) => {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node.enum) && node.enum.length === 0) delete node.enum;
  if (Array.isArray(node.enumNames) && node.enumNames.length === 0) delete node.enumNames;
  if (Array.isArray(node.oneOf) && node.oneOf.length === 0) delete node.oneOf;
  if (node.properties) Object.values(node.properties).forEach(scrubEmptyEnums);
  if (node.items) scrubEmptyEnums(node.items);
  if (node.$defs) Object.values(node.$defs).forEach(scrubEmptyEnums);
  if (node.definitions) Object.values(node.definitions).forEach(scrubEmptyEnums);
  if (node.anyOf) node.anyOf.forEach(scrubEmptyEnums);
  if (node.oneOf) node.oneOf.forEach(scrubEmptyEnums);
  if (node.allOf) node.allOf.forEach(scrubEmptyEnums);
};
const hasType = (doc, target) => {
  const t = String(target || '').toLowerCase();
  const val = doc.recipe_types ?? doc.types ?? doc.type ?? doc.recipeType;
  if (Array.isArray(val)) return val.map(x => String(x).toLowerCase()).includes(t);
  if (val && typeof val === 'object') return Boolean(val[t]) || Object.keys(val).some(k => k.toLowerCase() === t && val[k]);
  if (typeof val === 'string') return val.toLowerCase().includes(t);
  return false;
};

/* ---------- ImageUploads custom field ---------- */
function ImageUploadsField({ formData = [], onChange, formContext }) {
  const name = (formContext?.formData?.name || '').trim();
  const nameSet = !!name;

  const [images, setImages] = useState(Array.isArray(formData) ? formData : []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => setImages(Array.isArray(formData) ? formData : []), [formData]);

  const storage = getStorage();
  const folderPrefix = `dessert_recipes/images/${slug(name || 'unnamed')}`;

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setErr('');
    const next = [...images];
    try {
      for (const file of files) {
        const path = `${folderPrefix}/${Date.now()}-${slugFile(file.name)}`;
        const ref = storageRef(storage, path);
        const meta = { contentType: file.type || 'image/jpeg', cacheControl: 'public,max-age=31536000,immutable' };
        await new Promise((res, rej) =>
          uploadBytesResumable(ref, file, meta).on('state_changed', null, rej, res)
        );
        const url = await getDownloadURL(ref);
        next.push({ path, url, caption: '' });
      }
      setImages(next); onChange(next);
    } catch (ex) {
      setErr(ex?.message || 'Upload failed.');
    } finally {
      e.target.value = '';
      setBusy(false);
    }
  };

  const removeAt = async (idx) => {
    const item = images[idx];
    const next = images.slice(0, idx).concat(images.slice(idx + 1));
    setImages(next); onChange(next);
    if (item?.path) { try { await deleteObject(storageRef(storage, item.path)); } catch {} }
  };

  const updateCaption = (idx, caption) => {
    const next = images.map((x, i) => (i === idx ? { ...x, caption } : x));
    setImages(next); onChange(next);
  };

  const disabled = busy || !nameSet;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" component="label" disabled={disabled}>
          Upload Images
          <input hidden type="file" accept="image/*" multiple onChange={handleFiles} disabled={disabled} />
        </Button>
        {!nameSet && <Typography variant="body2" color="text.secondary">Enter a Name to enable uploads</Typography>}
      </Stack>

      {busy && <LinearProgress sx={{ mb: 2 }} />}
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Stack spacing={1}>
        {images.map((img, i) => (
          <Paper key={img.path || img.url || i} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <img src={img.url} alt={img.caption || `image-${i}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }} />
              <TextField size="small" label="Caption" fullWidth value={img.caption || ''} onChange={(e) => updateCaption(i, e.target.value)} />
              <IconButton color="error" onClick={() => removeAt(i)}><DeleteIcon /></IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

/* --------------- main component --------------- */
export default function DessertRecipeManager() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [containers, setContainers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Categories: only Dessert
        const catSnap = await getDocs(query(collection(db, 'recipe_categories'), orderBy('name')));
        const catsRaw = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const catsDessert = catsRaw
          .filter(c => !c.archived && hasType(c, 'dessert'))
          .map(c => ({ id: c.id, name: c.name ?? c.label ?? c.id, ...c }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(catsDessert);

        // Sizes
        const sizeSnap = await getDocs(query(collection(db, 'beverage_sizes'), orderBy('label')));
        setSizes(sizeSnap.docs.map(d => ({ id: d.id, label: d.data().label ?? d.id, ...d.data() })));

        // Equipment
        const toolSnap = await getDocs(query(collection(db, 'equipment_tools'), orderBy('name')));
        setEquipment(toolSnap.docs.map(d => ({ id: d.id, label: d.data().name ?? d.id, ...d.data() })));

        // INGREDIENTS: filter to Dessert-tagged only
        const ingSnap = await getDocs(query(collection(db, 'recipe_ingredients'), orderBy('name')));
        const ingsRaw = ingSnap.docs.map(d => ({ id: d.id, label: d.data().name ?? d.id, ...d.data() }));
        const ingsDessert = ingsRaw.filter(i => {
          const types = Array.isArray(i.types) ? i.types : i.type ? [i.type] : [];
          return types.map(t => String(t).toLowerCase()).includes('dessert');
        });
        setIngredients(ingsDessert);

        // Units
        const unitSnap = await getDocs(query(collection(db, 'ingredient_units'), orderBy('label')));
        setUnits(unitSnap.docs.map(d => ({ id: d.id, label: d.data().label ?? d.id, ...d.data() })));

        // Containers
        const contSnap = await getDocs(query(collection(db, 'packaging_containers'), orderBy('label')));
        setContainers(contSnap.docs.map(d => ({ id: d.id, label: d.data().label ?? d.id, ...d.data() })));

        setErr('');
      } catch (ex) {
        setErr(ex?.message || 'Failed to load reference data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* --------------- dynamic schema --------------- */
  const schema = useMemo(() => {
    const s = deepClone(baseSchema);

    // require category
    if (!Array.isArray(s.required)) s.required = [];
    if (!s.required.includes('category')) s.required.push('category');

    // category choices
    if (s?.properties?.category) {
      if (categories.length > 0) {
        applyChoices(s.properties.category, categories, 'id', ['name', 'label', 'id']);
      } else {
        delete s.properties.category.enum;
        delete s.properties.category.enumNames;
        delete s.properties.category.oneOf;
      }
    }

    // fill choices everywhere ingredient_id/unit_id/size_id/container_id/equipment_id appear
    const fill = (node) => {
      if (!node || typeof node !== 'object') return;
      if (node.properties) {
        const p = node.properties;
        if (p.ingredient_id) applyChoices(p.ingredient_id, ingredients, 'id', ['label','name','id']);
        if (p.unit_id)       applyChoices(p.unit_id,       units,       'id', ['label','name','id']);
        if (p.size_id)       applyChoices(p.size_id,       sizes,       'id', ['label','name','id']);
        if (p.container_id)  applyChoices(p.container_id,  containers,  'id', ['label','name','id']);
        if (p.equipment_id)  applyChoices(p.equipment_id,  equipment,   'id', ['label','name','id']);
      }
      if (node.items) fill(node.items);
      if (node.properties) Object.values(node.properties).forEach(fill);
      if (node.$defs) Object.values(node.$defs).forEach(fill);
      if (node.definitions) Object.values(node.definitions).forEach(fill);
      if (node.anyOf) node.anyOf.forEach(fill);
      if (node.oneOf) node.oneOf.forEach(fill);
      if (node.allOf) node.allOf.forEach(fill);
    };

    fill(s);
    scrubEmptyEnums(s);
    return s;
  }, [baseSchema, categories, sizes, equipment, ingredients, units, containers]);

  /* --------------- ui schema --------------- */
  const uiSchema = useMemo(() => ({
    id:         { 'ui:widget': 'hidden' },
    archived:   { 'ui:widget': 'hidden' },
    version:    { 'ui:widget': 'hidden' },
    updated_at: { 'ui:widget': 'hidden' },
    created_at: { 'ui:widget': 'hidden' },
    recipe_type:{ 'ui:widget': 'hidden' },

    images: { 'ui:field': 'ImageUploads' },

    ingredients: {
      items: {
        ingredient_id: { 'ui:widget': 'select' },
        unit_id:       { 'ui:widget': 'select' }
      }
    },

    servings: {
      items: {
        size_id: { 'ui:widget': 'select' },
        packaging: {
          to_go:   { container_id: { 'ui:widget': 'select' } },
          dine_in: { container_id: { 'ui:widget': 'select' } }
        },
        overrides: {
          ingredients: {
            items: {
              ingredient_id: { 'ui:widget': 'select' },
              unit_id:       { 'ui:widget': 'select' }
            }
          }
        }
      }
    },

    equipment: { items: { equipment_id: { 'ui:widget': 'select' } } },

    category: (categories.length > 0)
      ? { 'ui:widget': 'select' }
      : {
          'ui:disabled': true,
          'ui:placeholder': 'No Dessert categories configured',
          'ui:help': 'Create a Dessert category in Category Manager to enable this field.'
        }
  }), [categories.length]);

  /* --------------- save prep --------------- */
  const onBeforeSave = useCallback((doc) => {
    const out = { ...doc };
    const nowIso = new Date().toISOString();

    // normalize recipe type & timestamps
    delete out.type;
    delete out.recipeType;

    out.recipe_type = 'dessert';
    out.updated_at = nowIso;
    if (!out.created_at) out.created_at = nowIso;
    if (typeof out.archived !== 'boolean') out.archived = false;

    // clean empty arrays
    ['servings','equipment','ingredients','procedure','toppings','quality_checks','safety','images','notes','tags']
      .forEach(k => { if (Array.isArray(out[k]) && out[k].length === 0) delete out[k]; });

    return out;
  }, []);

  /* --------------- render --------------- */
  if (loading) return (<Box p={2}><LinearProgress /></Box>);
  if (err) return (<Box p={2}><Alert severity="error">{err}</Alert></Box>);

  return (
    <RecipeManagerV2
      schema={schema}
      uiSchema={uiSchema}
      collectionName="dessert_recipes"
      defaultRecipeType="dessert"
      onBeforeSave={onBeforeSave}
      fields={{ ImageUploads: ImageUploadsField }}
    />
  );
}
